/**
 * Pure parser for a `/auth/return` arrival. Dependency- and side-effect-free (no
 * `window`, no store writes) so it unit-tests in isolation.
 *
 * saas-auth delivers the session in one of two modes and the SPA must handle BOTH
 * (genesis handoff §3.3 — legacy is live by default today, code-delivery flips per host):
 *  - **legacy:** `?token=&accessToken=&refreshToken=&expiresIn=` (`token` mirrors
 *    `accessToken`). The URL `refreshToken` is deliberately NOT surfaced here — the
 *    HttpOnly cookie is authoritative and the URL copy must never be stored.
 *  - **code:** `?code=<opaque>`, traded via `POST /api/auth/session/exchange`.
 *  - **error:** `?error=oauth_failed&errorCode=<CODE>`.
 *
 * The URL FRAGMENT is checked before the query: an MFA hand-off delivers tokens in the
 * fragment so they never reach server logs (ported from saas-admin's `returnTokens`).
 */
export type AuthReturnArrival =
  | { mode: 'legacy'; accessToken: string; expiresIn: number | null }
  | { mode: 'code'; code: string }
  | { mode: 'error'; error: string; errorCode: string | null }
  | { mode: 'none' };

export function parseAuthReturn(search: string, hash: string): AuthReturnArrival {
  const fragment = new URLSearchParams(hash.replace(/^#/, ''));
  const query = new URLSearchParams(search.replace(/^\?/, ''));

  for (const params of [fragment, query]) {
    const arrival = readArrival(params);
    if (arrival) return arrival;
  }
  return { mode: 'none' };
}

function readArrival(p: URLSearchParams): AuthReturnArrival | null {
  const error = p.get('error');
  if (error) return { mode: 'error', error, errorCode: p.get('errorCode') };

  const accessToken = p.get('accessToken') ?? p.get('token');
  if (accessToken) return { mode: 'legacy', accessToken, expiresIn: readExpiresIn(p) };

  const code = p.get('code');
  if (code) return { mode: 'code', code };

  return null;
}

function readExpiresIn(p: URLSearchParams): number | null {
  const raw = p.get('expiresIn');
  if (raw === null) return null;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}
